import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@mui/material';
import concatClassNames from 'utils/classNames';
import { isUserGeneralAdministrator, isUserMarketingAdministrator, isUserRegularAdministrator } from 'utils/auth';
import { useGetOrphanedShipmentCountQuery } from 'store/api/orderApi';
import { type IClassName } from 'types/common/Props';
import AdminBlogTab from 'components/administration/blog/AdminBlogTab';
import AdminUsersTab from 'components/administration/users/AdminUsersTab';
import AdminProductsTab from 'components/administration/products/AdminProductsTab';
import AdminOrdersTab from 'components/administration/orders/AdminOrdersTab';
import AdminPaymentsTab from 'components/administration/payments/AdminPaymentsTab';
import AdminBannerTabs from 'components/administration/banners/AdminBannerTabs';
import AdminReviewsTab from 'components/administration/reviews/AdminReviewsTab';
import AdminPagesTab from '../../components/administration/pages/AdminPagesTab';
import AdminCategoriesTab from '../../components/administration/categories/AdminCategoriesTab';
import AdminDeliveryPricingsTab from '../../components/administration/delivery/AdminDeliveryPricingsTab';
import AdminPayoutsTab from 'components/administration/payouts/AdminPayoutsTab';

import Tabs from 'components/tabs/Tabs';
import './Administration.scss';

interface IAdministrationProps extends IClassName {
}

enum AdministrationTabs {
  Blog = 'Blog',
  Banners = 'Banners',
  Users = 'Users',
  Orders = 'Orders',
  Products = 'Products',
  Payments = 'Payments',
  Reviews = 'Reviews',
  Pages = 'Pages',
  Categories = 'Categories',
  DeliveryPricings = 'DeliveryPricings',
  Payouts = 'Payouts'
}

const Administration = ({ className }: IAdministrationProps) => {
  const { t } = useTranslation();
  const { data: orphanedCount } = useGetOrphanedShipmentCountQuery(undefined, {
    skip: !isUserGeneralAdministrator()
  });

  const tabs = useMemo(() => {
    const tabsArray = [];

    if (isUserGeneralAdministrator() || isUserRegularAdministrator()) {
      tabsArray.push({
        label: AdministrationTabs.Users,
        content: <AdminUsersTab/>,
        name: t('administration.users.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Orders,
        content: <AdminOrdersTab/>,
        name: t('administration.orders.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Payouts,
        content: <AdminPayoutsTab/>,
        name: t('administration.payouts.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Products,
        content: <AdminProductsTab/>,
        name: t('administration.products.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Reviews,
        content: <AdminReviewsTab/>,
        name: t('administration.reviews.title')
      });
    }

    if (isUserGeneralAdministrator()) {
      tabsArray.push({
        label: AdministrationTabs.Payments,
        content: <AdminPaymentsTab/>,
        name: t('administration.payments.title')
      });
      tabsArray.push({
        label: AdministrationTabs.DeliveryPricings,
        content: <AdminDeliveryPricingsTab/>,
        name: t('administration.deliveryPricings.title')
      });
    }

    if (isUserGeneralAdministrator() || isUserRegularAdministrator() || isUserMarketingAdministrator()) {
      tabsArray.push({
        label: AdministrationTabs.Categories,
        content: <AdminCategoriesTab/>,
        name: t('administration.categories.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Blog,
        content: <AdminBlogTab/>,
        name: t('administration.blog.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Banners,
        content: <AdminBannerTabs/>,
        name: t('administration.banners.title')
      });
      tabsArray.push({
        label: AdministrationTabs.Pages,
        content: <AdminPagesTab/>,
        name: t('administration.pages.title')
      });
    }

    return tabsArray;
  }, []);

  return (
    <section className={concatClassNames(className, 'administration')}>
      <h1 className="text-center fs-31 administration-title">
        {t('administration.title')}
      </h1>
      {orphanedCount !== undefined && orphanedCount > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('administration.orphanedShipments.alert', { count: orphanedCount })}
        </Alert>
      )}
      <Tabs tabsData={tabs} ariaLabel="administration tabs"/>
    </section>
  );
};

export default Administration;
